/**
 * [OBSZAR 20, 2026-09-18] JEDNA logika stosowania zdarzeń synchronizacji do listy powiadomień.
 *
 * CO BYŁO ŹLE: zdarzenia „przeczytane / skasowane na innym urządzeniu" (`NotificationRead`, `NotificationsAllRead`,
 * `NotificationRemoved`, `NotificationRemovedByLink`) stosował WYŁĄCZNIE NotificationContext — do listy dzwonka.
 * Centrum powiadomień (/powiadomienia) trzyma WŁASNĄ listę pobraną z API i nie słuchało niczego poza
 * `notification-received`, którego zresztą nikt nigdy nie wysyłał. Człowiek z otwartym centrum na PC odczytywał
 * powiadomienie na laptopie i na PC widział je dalej jako nieprzeczytane — dokładnie objaw ze zgłoszenia.
 *
 * TERAZ NotificationContext re-emituje każde zdarzenie huba jako zdarzenie okna (nazwy niżej), a każda lista, która
 * chce być zgodna z dzwonkiem, podpina się jednym hookiem. Reguła zastosowania jest w JEDNEJ funkcji `zastosujSync`
 * — dzwonek i centrum nie mogą się rozjechać, bo nie mają osobnych kopii tej reguły.
 */
import { useEffect } from "react";
import { KATEGORIA_AKCEPTACJE, KODY_AKCEPTACJI } from "../constants/notificationCategories";

export const ZDARZENIA_SYNC = Object.freeze({
    received: "notification-received",
    read: "notification-read",
    // [2026-09-30, polecenie B3] Odczyt po OBIEKCIE: serwer oznacza naraz wszystkie pozycje wskazujące tę samą
    // sprawę (otwarcie karty albo klik w jedno z kilku powiadomień o niej) i wysyła JEDNĄ listę identyfikatorów.
    readMany: "notifications-read-many",
    allRead: "notifications-all-read",
    removed: "notification-removed",
    removedByLink: "notification-removed-by-link",
    pruned: "notifications-pruned",
});

/* [2026-09-30, polecenie właściciela B3] KLUCZ OBIEKTU z linku powiadomienia — lustro serwerowego
   `DogSoft.Application.Notifications.KluczObiektuPowiadomienia` (ta sama biała lista rodzajów i te same kształty linków).
   Służy WYŁĄCZNIE optymistycznemu wygaszeniu pozycji w dzwonku po otwarciu obiektu; o tym, co jest przeczytane,
   rozstrzyga serwer (`NotificationsReadMany` + autorytatywny licznik). Link bez identyfikatora → null. */
const RODZAJE_OBIEKTU = new Set([
    "zgloszenie", "projekt", "grupa-projektow", "szkolenie", "aktualnosc", "konkurs", "ankieta", "artykul-kb",
    "dokument", "tablica-kanban", "onboarding", "rozliczenie-delegacji", "komunikat", "wniosek-hr",
]);
const WZOR_GUID = /^\{?([0-9a-f]{8})-?([0-9a-f]{4})-?([0-9a-f]{4})-?([0-9a-f]{4})-?([0-9a-f]{12})\}?$/i;
const WZOR_ID = /^[a-z0-9][a-z0-9_-]*$/;

export const kluczObiektu = (rodzaj, id) => {
    if (rodzaj == null || id == null) return null;
    const r = String(rodzaj).trim().toLowerCase();
    const s = String(id).trim();
    if (!RODZAJE_OBIEKTU.has(r) || s.length === 0 || s.length > 200) return null;
    if (r === "zgloszenie") {
        const m = WZOR_GUID.exec(s);
        return m ? `${r}:${m.slice(1).join("-").toLowerCase()}` : null;
    }
    const lower = s.toLowerCase();
    return WZOR_ID.test(lower) ? `${r}:${lower}` : null;
};

export const kluczObiektuZLinku = (link) => {
    if (typeof link !== "string" || link.trim() === "") return null;
    let s = link.trim();
    if (/^https?:\/\//i.test(s)) {
        try { const u = new URL(s); s = u.pathname + u.search; } catch { return null; }
    }
    const hash = s.indexOf("#");
    if (hash >= 0) s = s.slice(0, hash);
    const q = s.indexOf("?");
    const sciezka = q >= 0 ? s.slice(0, q) : s;
    const params = new URLSearchParams(q >= 0 ? s.slice(q + 1) : "");
    const param = (nazwa) => {
        for (const [k, v] of params.entries()) {
            if (k.toLowerCase() === nazwa.toLowerCase()) return v.trim() || null;
        }
        return null;
    };
    let seg;
    try {
        seg = sciezka.split("/").filter(Boolean).map((x) => decodeURIComponent(x).trim().toLowerCase());
    } catch { return null; }
    if (seg.length === 0) return null;
    const k = (rodzaj, id) => (id == null ? null : kluczObiektu(rodzaj, id));

    if (seg.length >= 3 && seg[0].startsWith("obsluga-") && seg[1] === "zgloszenia") return k("zgloszenie", seg[2]);
    if (seg.length >= 3 && seg[0] === "it-management" && seg[1] === "tickets") return k("zgloszenie", seg[2]);
    if (seg.length >= 2 && (seg[0] === "tickets" || seg[0] === "zgloszenia")) return k("zgloszenie", seg[1]);
    if (seg.length === 1 && (seg[0] === "moje-zgloszenia" || seg[0] === "my-tickets")) return k("zgloszenie", param("ticketId"));

    switch (seg[0]) {
        case "projekty":
            if (seg.length >= 3 && seg[1] === "grupa") return k("grupa-projektow", seg[2]);
            return seg.length >= 2 && seg[1] !== "grupa" ? k("projekt", seg[1]) : null;
        case "szkolenia": return seg.length >= 2 ? k("szkolenie", seg[1]) : null;
        case "aktualnosci": return seg.length >= 2 ? k("aktualnosc", seg[1]) : null;
        case "konkursy": return seg.length >= 2 ? k("konkurs", seg[1]) : null;
        case "ankiety": return seg.length >= 2 ? k("ankieta", seg[1]) : null;
        case "baza-wiedzy": return seg.length >= 2 ? k("artykul-kb", seg[1]) : null;
        case "dokumenty":
        case "polityki": return seg.length === 1 ? k("dokument", param("doc")) : null;
        case "kanban": return seg.length === 1 ? k("tablica-kanban", param("board")) : null;
        case "obsluga-hr": return seg.length >= 3 && seg[1] === "onboarding" ? k("onboarding", seg[2]) : null;
        // [domknięcie B3 2026-10-01] `?rozliczenie=` — rozliczenie delegacji; `?req=` — sam wniosek (szuflada).
        case "wnioski-hr":
            if (seg.length !== 1) return null;
            return param("rozliczenie") != null ? k("rozliczenie-delegacji", param("rozliczenie")) : k("wniosek-hr", param("req"));
        case "centrum-powiadomien": return seg.length === 1 ? k("komunikat", param("komunikat")) : null;
        default: return null;
    }
};

/** Czy link powiadomienia wskazuje obiekt (rodzaj, id) — porównanie kluczy, nie podciągów (id „12” ≠ „/projekty/123”). */
export const linkWskazujeObiekt = (link, rodzaj, id) => {
    const cel = kluczObiektu(rodzaj, id);
    return cel != null && kluczObiektuZLinku(link) === cel;
};

/* [2026-09-30, B3 — przegląd adwersarialny] SPRAWA DO DECYZJI NIE JEST „PRZECZYTANA” PRZEZ OTWARCIE OBIEKTU.
   Rodzina „Do akceptacji” (kategoria z bazy albo kod z KODY_AKCEPTACJI — to samo kryterium co zakładka „Do decyzji”
   w NotificationPanel i czyAkceptacja w usePendingApprovals) jest wyłączona z odczytu po obiekcie i z rodzeństwa
   klikniętej pozycji: klik w pl_item_assigned o projekcie nie może zdjąć z „Do decyzji” prośby o dostęp do tego
   projektu (pl_access_requested ma ten sam link). Lustro serwerowego KluczObiektuPowiadomienia.CzekaNaDecyzje. */
export const czekaNaDecyzjeWOdczycie = (n) =>
    n?.typeCategory === KATEGORIA_AKCEPTACJE || KODY_AKCEPTACJI.includes(n?.typeCode);

/* ⚠️ `new Date(null).getTime()` to 0 (rok 1970), nie NaN — pozycja BEZ daty wyglądałaby na starszą od każdego
   progu i retencja zdjęłaby ją z ekranu. Brak daty = nie wiemy = zostaje. Złapane własną zapadką. */
const czas = (w) => {
    if (w == null || w === "") return null;
    const t = new Date(w).getTime();
    return Number.isFinite(t) ? t : null;
};

/**
 * Czysta funkcja: lista + zdarzenie → nowa lista. Bez skutków ubocznych, do użycia w `setState(prev => …)`.
 * ⚠️ Identyfikatory porównujemy przez `String(...)`: z serwera przychodzi Guid, a lista trzyma też pozycje
 * syntetyczne z identyfikatorem tekstowym (prefiks „hr_").
 */
export const zastosujSync = (lista, nazwa, detail) => {
    if (!Array.isArray(lista)) return lista;
    switch (nazwa) {
        case ZDARZENIA_SYNC.read: {
            if (detail == null) return lista;
            const id = String(detail);
            return lista.map((n) => (String(n.id) === id
                ? { ...n, isRead: true, readAt: n.readAt || new Date().toISOString() }
                : n));
        }
        case ZDARZENIA_SYNC.readMany: {
            // Lista identyfikatorów (Guid z serwera). Pusta albo nie-tablica = nic do zmiany — lista zostaje TA SAMA.
            if (!Array.isArray(detail) || detail.length === 0) return lista;
            const ids = new Set(detail.filter((x) => x != null).map((x) => String(x)));
            if (ids.size === 0) return lista;
            return lista.map((n) => (ids.has(String(n.id)) && !n.isRead
                ? { ...n, isRead: true, readAt: n.readAt || new Date().toISOString() }
                : n));
        }
        case ZDARZENIA_SYNC.allRead:
            return lista.map((n) => (n.isRead ? n : { ...n, isRead: true, readAt: new Date().toISOString() }));
        case ZDARZENIA_SYNC.removed: {
            if (detail == null) return lista;
            const id = String(detail);
            return lista.filter((n) => String(n.id) !== id);
        }
        case ZDARZENIA_SYNC.removedByLink:
            // Pusty odnośnik jest ignorowany — inaczej jedno zdarzenie bez danych zdjęłoby wszystkie pozycje BEZ linku.
            if (!detail) return lista;
            return lista.filter((n) => n.link !== detail);
        case ZDARZENIA_SYNC.pruned: {
            /* Retencja skasowała w bazie: przeczytane starsze niż `readCutoff` i WSZYSTKO starsze niż `hardCutoff`.
               Zdejmujemy DOKŁADNIE tym samym warunkiem — pozycja bez daty zostaje (nie wiemy, więc nie zgadujemy). */
            const readCutoff = czas(detail?.readCutoff);
            const hardCutoff = czas(detail?.hardCutoff);
            if (readCutoff == null && hardCutoff == null) return lista;
            return lista.filter((n) => {
                const t = czas(n.createdAt);
                if (t == null) return true;
                if (hardCutoff != null && t < hardCutoff) return false;
                if (readCutoff != null && n.isRead && t < readCutoff) return false;
                return true;
            });
        }
        default:
            return lista;
    }
};

/**
 * Podpina listę pod zdarzenia synchronizacji z innych urządzeń. `setLista` musi być stabilne (setState z useState).
 * Zdarzenie `received` celowo NIE jest tu obsługiwane: wstawienie nowej pozycji zależy od tego, co dana lista
 * pokazuje (dzwonek dokłada na górę zawsze, centrum tylko na zakładce powiadomień) — to decyzja strony, nie hooka.
 */
export function useSynchronizacjaPowiadomien(setLista) {
    useEffect(() => {
        if (typeof setLista !== "function") return undefined;
        const nazwy = [
            ZDARZENIA_SYNC.read, ZDARZENIA_SYNC.readMany, ZDARZENIA_SYNC.allRead, ZDARZENIA_SYNC.removed,
            ZDARZENIA_SYNC.removedByLink, ZDARZENIA_SYNC.pruned,
        ];
        const obsluga = (e) => {
            setLista((prev) => zastosujSync(prev, e.type, e?.detail));
        };
        nazwy.forEach((n) => window.addEventListener(n, obsluga));
        return () => { nazwy.forEach((n) => window.removeEventListener(n, obsluga)); };
    }, [setLista]);
}
